import type { FirefliesTranscript } from '../types';

const FIREFLIES_GRAPHQL_URL = 'https://api.fireflies.ai/graphql';

const TRANSCRIPTS_LIST_QUERY = `
  query Transcripts($limit: Int, $skip: Int, $fromDate: DateTime, $toDate: DateTime, $participants: [String!]) {
    transcripts(limit: $limit, skip: $skip, fromDate: $fromDate, toDate: $toDate, participants: $participants) {
      id
      title
      date
      transcript_url
      duration
      participants
      summary {
        overview
        action_items
        short_summary
        keywords
        topics_discussed
      }
      meeting_attendees {
        displayName
        email
        name
      }
    }
  }
`;

const TRANSCRIPT_DETAIL_QUERY = `
  query Transcript($transcriptId: String!) {
    transcript(id: $transcriptId) {
      id
      title
      date
      dateString
      transcript_url
      duration
      participants
      summary {
        overview
        action_items
        short_summary
        keywords
        topics_discussed
      }
      sentences {
        index
        speaker_name
        speaker_id
        text
      }
      meeting_attendees {
        displayName
        email
        name
      }
    }
  }
`;

const getDateRange = () => {
  const now = new Date();
  const toDate = now.toISOString();
  const fromDate = new Date(now);
  fromDate.setDate(fromDate.getDate() - 30);
  return { fromDate: fromDate.toISOString(), toDate };
};

export const fetchRecentTranscripts = async (
  apiKey: string,
  limit: number = 20,
  participantEmail?: string,
): Promise<FirefliesTranscript[]> => {
  const { fromDate, toDate } = getDateRange();
  const variables: Record<string, unknown> = {
    limit,
    skip: 0,
    fromDate,
    toDate,
  };
  if (participantEmail?.trim()) {
    variables.participants = [participantEmail.trim()];
  }

  const response = await fetch(FIREFLIES_GRAPHQL_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      query: TRANSCRIPTS_LIST_QUERY,
      variables,
    }),
  });

  const json = await response.json();

  if (!response.ok) {
    throw new Error(`Fireflies API error: ${response.status} ${response.statusText}`);
  }

  if (json.errors) {
    throw new Error(json.errors[0]?.message || 'Fireflies API error');
  }

  return json.data?.transcripts ?? [];
};

export const fetchTranscriptDetail = async (
  apiKey: string,
  transcriptId: string,
): Promise<FirefliesTranscript | null> => {
  const response = await fetch(FIREFLIES_GRAPHQL_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      query: TRANSCRIPT_DETAIL_QUERY,
      variables: { transcriptId },
    }),
  });

  if (!response.ok) {
    throw new Error(`Fireflies API error: ${response.status} ${response.statusText}`);
  }

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0]?.message || 'Fireflies API error');
  }

  return json.data?.transcript ?? null;
};
